import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isMobileScreen } from '@apply/ui';
import { Button, ButtonTheme, Panel, PanelBorderRadius, PanelTheme, GenericAlertPopup } from '@apply/components';
import { Footer } from '../common_v2/components/Footer';
import { LogoLink } from '../common_v2/components/LogoLink';
import { useAuthentication } from '../Login/hooks/useAuthentication';
import { useAuthenticationStore } from '../state/authenticationStore';
import { DrawApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import { IGCUserEntity } from '@apply/api/src/api/DrawApi';
import './scss/entityselectionpage.scss';

export const EntitySelectionPage: React.FC = () => {
    const navigate = useNavigate();
    const { authentication, logout } = useAuthentication();
    const set_authorizedDrawUserSelectedEntity = useAuthenticationStore((s) => s.set_selectedEntity);
    const [gcDrawEntities, setGCDrawEntities] = useState<IGCUserEntity[]>([]);
    const [gcOwnedEntity, setGCOwnedEntity] = useState<IGCUserEntity | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [entitiesError, setEntitiesError] = useState<boolean>(false);
    const [acceptingEntityId, setAcceptingEntityId] = useState<number | null>(null);

    const fetchEntities = async () => {
        setLoading(true);
        setEntitiesError(false);
        try {
            const drawApi = new DrawApi.DrawApi(applicationInsights);
            const response = await drawApi.getGCEntities();

            if (response.statusCode === 200) {
                setGCDrawEntities(response.result.drawEntities);
                setGCOwnedEntity(response.result.ownedEntity || null);
            } else {
                console.error('Failed to fetch entities:', response.errors);
                setEntitiesError(true);
                setGCDrawEntities([]);
                setGCOwnedEntity(null);
            }
        } catch (error) {
            console.error('Error fetching entities:', error);
            setEntitiesError(true);
            setGCDrawEntities([]);
            setGCOwnedEntity(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntities();
    }, []);

    const handleEntitySelect = (entity: IGCUserEntity) => {
        set_authorizedDrawUserSelectedEntity(entity);
        navigate('/home');
    };

    const handleEntityAccept = async (entity: IGCUserEntity) => {
        if (!entity.authorizedDrawUserId) {
            console.error('No authorized draw user ID for entity:', entity);
            return;
        }

        setAcceptingEntityId(entity.authorizedDrawUserId);
        try {
            const drawApi = new DrawApi.DrawApi(applicationInsights);
            const response = await drawApi.acceptGCAuthorization(entity.authorizedDrawUserId);

            if (response.statusCode === 200) {
                // Set the entity and navigate to home
                set_authorizedDrawUserSelectedEntity(entity);
                navigate('/home');
            } else {
                console.error('Failed to accept entity:', response.errors);
                setAcceptingEntityId(null);
                throw new Error('Failed to accept entity. Please try again.');
            }
        } catch (error) {
            console.error('Error accepting entity:', error);
            const errorMessage = error instanceof Error && error.message
                ? error.message
                : 'An error occurred while accepting the entity. Please try again.';
            GenericAlertPopup({
                title: 'Error',
                children: errorMessage,
                width: '350px',
            });
            setAcceptingEntityId(null);
        }
    };

    const handleBusinessOwnerSelect = () => {
        set_authorizedDrawUserSelectedEntity(null);
        navigate('/home');
    };

    const handleSetupOrganization = () => {
        const gcEmail = authentication?.user?.email;
        if (!gcEmail) return;

        logout();
        navigate(`/gc-register?GCEmail=${encodeURIComponent(gcEmail)}`);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const panelChildren = () => {
        return (
            <>
                <div className={`preAuthenticationLogoLink ${isMobileScreen() ? 'mobile' : ''}`}>
                    <LogoLink />
                </div>
                <main>
                    <div className='entitySelection-page'>
                        <h2>Choose Business/Role</h2>
                        <br />
                        {loading ? (
                            <div className='loading'>
                                <span className='spinner'></span>
                                <span className='spinner-text'>Loading</span>
                            </div>
                        ) : (
                            <>
                                {authentication?.user.isRegistered && (
                                    <>
                                        <div className='primaryActions'>
                                            <div className='primaryActions-inner'>
                                                <div className='business-owner-button-wrapper'>
                                                    <Button
                                                        id='business-owner-entity'
                                                        theme={ButtonTheme.Choice}
                                                        width='100%'
                                                        handleClick={handleBusinessOwnerSelect}
                                                    >
                                                        <div className='title'>{gcOwnedEntity?.name || 'Home Page'}</div>
                                                        <div className='subTitle'>
                                                            {gcOwnedEntity?.userRole || 'Go to your Builder Finance home page'}
                                                        </div>
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='spacer'></div>
                                    </>
                                )}
                                {entitiesError ? (
                                    <div className='entityError'>
                                        <div className='errorMessage'>Unable to load your entities. Please try again.</div>
                                        <div className='primaryActions'>
                                            <Button
                                                id='retry-entities-button'
                                                theme={ButtonTheme.LinkBlue}
                                                width='auto'
                                                handleClick={fetchEntities}
                                            >
                                                Retry
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className='primaryActions'>
                                            <div className='primaryActions-inner'>
                                                {gcDrawEntities.length === 0 ? (
                                                    <div className='no-projects-message'>
                                                        <h4>You are not currently associated with any projects.</h4>
                                                    </div>
                                                ) : (
                                                    gcDrawEntities.map((entity) => {
                                                        const needsAcceptance = !entity.gcAcceptedUtc;
                                                        const isAccepting =
                                                            entity.authorizedDrawUserId !== undefined &&
                                                            acceptingEntityId === entity.authorizedDrawUserId;

                                                        return (
                                                            <div key={entity.ownerUserId} className='entity-item'>
                                                                {needsAcceptance ? (
                                                                    <div className='entity-needs-acceptance'>
                                                                        <div className='entity-info'>
                                                                            <div className='title'>{entity.name}</div>
                                                                            <div className='subTitle'>{entity.userRole}</div>
                                                                        </div>
                                                                        {isAccepting ? (
                                                                            <div className='accepting-spinner'>
                                                                                <span className='spinner'></span>
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                <div className='accept-button-container'>
                                                                                    <Button
                                                                                        id={`accept-entity-${entity.ownerUserId}`}
                                                                                        theme={ButtonTheme.Primary}
                                                                                        width='50%'
                                                                                        handleClick={() => handleEntityAccept(entity)}
                                                                                    >
                                                                                        Accept
                                                                                    </Button>
                                                                                </div>
                                                                                <div className='acceptance-info'>
                                                                                    If this is in error, please email us at{' '}
                                                                                    <a href='mailto:team@builderfinance.com'>
                                                                                        team@builderfinance.com
                                                                                    </a>
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <Button
                                                                        id={`entity-${entity.ownerUserId}`}
                                                                        theme={ButtonTheme.Choice}
                                                                        width='100%'
                                                                        handleClick={() => handleEntitySelect(entity)}
                                                                    >
                                                                        <div className='title'>{entity.name}</div>
                                                                        <div className='subTitle'>{entity.userRole}</div>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                                {!authentication?.user.isRegistered && (
                                    <>
                                        <br />
                                        <div className='loanApply'>
                                            <span>Looking to apply for a Loan?</span>
                                        </div>
                                        <div className='primaryActions'>
                                            <Button
                                                id='setup-organization-button'
                                                theme={ButtonTheme.Tertiary}
                                                width='100%'
                                                handleClick={handleSetupOrganization}
                                            >
                                                Setup My Organization
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        <br />
                        <div className='logout-container'>
                            <Button
                                id='logout-button'
                                theme={ButtonTheme.LinkBlue}
                                width='auto'
                                handleClick={handleLogout}
                            >
                                Logout
                            </Button>
                        </div>
                    </div>
                </main>
            </>
        );
    };

    return (
        <>
            <div className='app-container'>
                <div className='app-container authenticationContainer'>
                    {isMobileScreen() ? (
                        <div className='authenticationContainer mobile'>{panelChildren()}</div>
                    ) : (
                        <div className='authenticationContainer-desktop'>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Medium}
                                width={'90%'}
                            >
                                <div className='authenticationContainer-desktop-desktopWrapper'>{panelChildren()}</div>
                            </Panel>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};
